import { BookOpen, Check, Infinity as InfinityIcon, ScrollText } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Slider } from '@/components/ui/slider'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import { useAvailableReaderFonts } from '../hooks/useAvailableReaderFonts'
import { SettingRow } from '../shared/setting-controls'
import { ReaderFontPicker } from './reader-font-picker'
import { READER_FONT_OPTIONS } from './reader-fonts'
import {
  DEFAULT_READER_SETTINGS,
  type EpubFlow,
  getFontFamilyCSS,
  getThemeColors,
  type ReaderSettings,
  type ReaderTheme,
} from './reader-settings-types'

export type { EpubFlow, ReaderFont, ReaderSettings, ReaderTheme } from './reader-settings-types'
export { DEFAULT_READER_SETTINGS } from './reader-settings-types'

type Props = {
  open: boolean
  onClose: () => void
  settings: ReaderSettings
  onChange: (settings: ReaderSettings) => void
  defaultSettings?: ReaderSettings
  isVertical?: boolean
}

const FONT_SIZE_RANGE = { min: 12, max: 32 } as const

const FLOW_OPTIONS: {
  value: EpubFlow
  label: string
  description: string
  icon: ComponentType<{ className?: string }>
}[] = [
  { value: 'scrolled', label: 'スクロール', description: 'セクション単位でスクロール', icon: ScrollText },
  { value: 'continuous', label: '連続', description: 'セクションをまたいで連続スクロール', icon: InfinityIcon },
  { value: 'paginated', label: 'ページ', description: '1ページずつ表示', icon: BookOpen },
]

const THEME_OPTIONS: { value: ReaderTheme; label: string }[] = [
  { value: 'light', label: 'ライト' },
  { value: 'sepia', label: 'セピア' },
  { value: 'dark', label: 'ダーク' },
]

const PREVIEW_TEXT =
  '吾輩は猫である。名前はまだ無い。どこで生れたかとんと見当がつかぬ。何でも薄暗いじめじめした所でニャーニャー泣いていた事だけは記憶している。'

// --- 部品 ---

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <h3 className="pt-1 text-xs font-semibold tracking-wider text-muted-foreground">{children}</h3>
)

type SliderRowProps = {
  id: string
  label: string
  value: number
  display: string
  min: number
  max: number
  step: number
  onChange: (value: number) => void
}

const SliderRow = ({ id, label, value, display, min, max, step, onChange }: SliderRowProps) => (
  <SettingRow label={label} htmlFor={id}>
    <Slider id={id} value={[value]} onValueChange={([v]) => onChange(v)} min={min} max={max} step={step} />
    <span className="w-11 shrink-0 text-right text-xs tabular-nums text-muted-foreground">{display}</span>
  </SettingRow>
)

/** 現在の設定で本文がどう見えるかの見本 */
const ReaderPreview = ({ settings, isVertical }: { settings: ReaderSettings; isVertical: boolean }) => {
  const colors = getThemeColors(settings.theme)
  return (
    <div
      aria-hidden
      className="h-32 overflow-hidden rounded-lg border px-4 py-3 transition-colors"
      style={{
        backgroundColor: colors.backgroundColor,
        color: colors.textColor,
        fontFamily: getFontFamilyCSS(settings.fontFamily) ?? undefined,
        fontSize: settings.fontSize,
        lineHeight: settings.lineHeight,
        writingMode: isVertical ? 'vertical-rl' : undefined,
      }}
    >
      {PREVIEW_TEXT}
    </div>
  )
}

// --- 本体 ---

export const ReaderSettingsDialog = ({
  open,
  onClose,
  settings,
  onChange,
  defaultSettings,
  isVertical = false,
}: Props) => {
  const isFooterPinned = useUISettingsStore((state) => state.isReaderFooterPinned)
  const setFooterPinned = useUISettingsStore((state) => state.setReaderFooterPinned)
  const defaults = defaultSettings || DEFAULT_READER_SETTINGS
  const availableFonts = useAvailableReaderFonts()
  // 別端末で選んだフォントが URL で渡された場合も選択状態が見えるように残す
  const fontOptions = READER_FONT_OPTIONS.filter(
    (font) => font.value === settings.fontFamily || availableFonts.some((f) => f.value === font.value),
  )

  const updateSetting = <K extends keyof ReaderSettings>(key: K, value: ReaderSettings[K]) => {
    onChange({ ...settings, [key]: value })
  }

  const stepFontSize = (delta: number) => {
    const next = Math.min(FONT_SIZE_RANGE.max, Math.max(FONT_SIZE_RANGE.min, settings.fontSize + delta))
    updateSetting('fontSize', next)
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[90dvh] w-[calc(100vw-2rem)] gap-3 overflow-y-auto sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>表示設定</DialogTitle>
        </DialogHeader>

        <ReaderPreview settings={settings} isVertical={isVertical} />

        {/* 文字 */}
        <div className="grid gap-2.5">
          <SectionTitle>文字</SectionTitle>

          <SettingRow label="フォント" htmlFor="reader-font">
            <ReaderFontPicker
              id="reader-font"
              value={settings.fontFamily}
              options={fontOptions}
              onChange={(value) => updateSetting('fontFamily', value)}
            />
          </SettingRow>

          <SettingRow label="サイズ" htmlFor="reader-font-size">
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-xs"
              aria-label="文字を小さく"
              disabled={settings.fontSize <= FONT_SIZE_RANGE.min}
              onClick={() => stepFontSize(-1)}
            >
              A
            </Button>
            <Slider
              id="reader-font-size"
              value={[settings.fontSize]}
              onValueChange={([value]) => updateSetting('fontSize', value)}
              min={FONT_SIZE_RANGE.min}
              max={FONT_SIZE_RANGE.max}
              step={1}
            />
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-lg"
              aria-label="文字を大きく"
              disabled={settings.fontSize >= FONT_SIZE_RANGE.max}
              onClick={() => stepFontSize(1)}
            >
              A
            </Button>
            <span className="w-11 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {settings.fontSize}px
            </span>
          </SettingRow>

          <SliderRow
            id="reader-line-height"
            label="行間"
            value={settings.lineHeight}
            display={settings.lineHeight.toFixed(1)}
            min={1.0}
            max={3.0}
            step={0.1}
            onChange={(value) => updateSetting('lineHeight', value)}
          />
        </div>

        {/* テーマ */}
        <div className="grid gap-2.5 border-t pt-3">
          <SectionTitle>テーマ</SectionTitle>
          <div className="grid grid-cols-3 gap-2">
            {THEME_OPTIONS.map((option) => {
              const colors = getThemeColors(option.value)
              const selected = settings.theme === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => updateSetting('theme', option.value)}
                  className={cn(
                    'relative flex flex-col items-center gap-1 rounded-lg border py-2 transition-shadow',
                    selected
                      ? 'ring-2 ring-primary ring-offset-2 ring-offset-background'
                      : 'opacity-80 hover:opacity-100',
                  )}
                  style={{ backgroundColor: colors.backgroundColor, color: colors.textColor }}
                >
                  <span className="text-lg leading-none font-semibold">Aa</span>
                  <span className="text-[11px]">{option.label}</span>
                  {selected && (
                    <Check className="absolute top-1 right-1 size-3.5 rounded-full bg-primary p-0.5 text-primary-foreground" />
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* レイアウト */}
        <div className="grid gap-2.5 border-t pt-3">
          <SectionTitle>レイアウト</SectionTitle>

          <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
            {FLOW_OPTIONS.map((option) => {
              const Icon = option.icon
              const selected = settings.flow === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  title={option.description}
                  aria-pressed={selected}
                  onClick={() => updateSetting('flow', option.value)}
                  className={cn(
                    'flex flex-col items-center gap-1 rounded-md py-1.5 text-xs font-medium transition-colors',
                    selected
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="size-4" />
                  {option.label}
                </button>
              )
            })}
          </div>

          {/* 余白1 (横書き:左 / 縦書き:上)、余白2 (横書き:右 / 縦書き:下) */}
          <SliderRow
            id="reader-margin-start"
            label={isVertical ? '上余白' : '左余白'}
            value={settings.marginLeft}
            display={`${settings.marginLeft}px`}
            min={0}
            max={100}
            step={5}
            onChange={(value) => updateSetting('marginLeft', value)}
          />
          <SliderRow
            id="reader-margin-end"
            label={isVertical ? '下余白' : '右余白'}
            value={settings.marginRight}
            display={`${settings.marginRight}px`}
            min={0}
            max={100}
            step={5}
            onChange={(value) => updateSetting('marginRight', value)}
          />
        </div>

        {/* 操作 */}
        <div className="grid gap-2.5 border-t pt-3">
          <SectionTitle>操作</SectionTitle>
          <SettingRow label="スワイプ操作" htmlFor="reader-swipe" labelClassName="w-auto cursor-pointer">
            <Switch
              id="reader-swipe"
              checked={settings.swipeEnabled}
              onCheckedChange={(checked) => updateSetting('swipeEnabled', checked)}
            />
          </SettingRow>
          <SettingRow label="フッターを常に表示" htmlFor="reader-footer-pinned" labelClassName="w-auto cursor-pointer">
            <Switch id="reader-footer-pinned" checked={isFooterPinned} onCheckedChange={setFooterPinned} />
          </SettingRow>
        </div>

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" size="sm" className="w-full" onClick={() => onChange(defaults)}>
            初期設定に戻す
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
